import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

export interface Spalte<T> {
  schluessel: string;
  /** meist Text; auch Steuerelemente wie „alle auswählen“ */
  titel: ReactNode;
  breite?: string; // CSS-Grid-Spurbreite, z. B. "120px" oder "2fr"
  rechts?: boolean;
  /** auf schmalen Bildschirmen ausblenden */
  nebensache?: boolean;
  zelle: (zeile: T) => ReactNode;
}

interface DatenTabelleProps<T> {
  spalten: Spalte<T>[];
  zeilen: T[];
  zeilenId: (zeile: T) => string;
  /** Wird aufgerufen, wenn das Ende der geladenen Zeilen in Sicht kommt */
  mehrLaden?: () => void;
  hatMehr?: boolean;
  laedtMehr?: boolean;
  zeilenHoehe?: number;
  hoehe?: string;
  leer?: ReactNode;
  onZeile?: (zeile: T) => void;
  gewaehlt?: string | null;
  beschriftung: string;
}

const SCHMAL = '(max-width: 700px)';
function useSchmal(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(SCHMAL);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(SCHMAL).matches,
    () => false,
  );
}

/**
 * Virtualisierte Tabelle: rendert nur die sichtbaren Zeilen. Zusammen mit Cursor-Paginierung
 * bleibt die Liste bei 100 oder 10 Mio. Einträgen gleich schnell.
 */
export function DatenTabelle<T>({
  spalten: alleSpalten,
  zeilen,
  zeilenId,
  mehrLaden,
  hatMehr = false,
  laedtMehr = false,
  zeilenHoehe = 48,
  hoehe = 'calc(100vh - 240px)',
  leer,
  onZeile,
  gewaehlt = null,
  beschriftung,
}: DatenTabelleProps<T>) {
  const schmal = useSchmal();
  const spalten = schmal ? alleSpalten.filter((s) => !s.nebensache) : alleSpalten;
  const scrollRef = useRef<HTMLDivElement>(null);
  const kopfRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({
    count: zeilen.length + (hatMehr ? 1 : 0),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => zeilenHoehe,
    overscan: 12,
  });
  const elemente = v.getVirtualItems();
  const letzte = elemente.at(-1);

  useEffect(() => {
    if (letzte && letzte.index >= zeilen.length - 1 && hatMehr && !laedtMehr) mehrLaden?.();
  }, [letzte?.index, zeilen.length, hatMehr, laedtMehr, mehrLaden]);

  const raster = spalten.map((s) => s.breite ?? '1fr').join(' ');

  if (zeilen.length === 0 && !hatMehr) return <div className="dxl-tabelle__leer">{leer}</div>;

  return (
    <div className="dxl-tabelle" role="table" aria-label={beschriftung} aria-rowcount={zeilen.length + 1}>
      <div ref={kopfRef} className="dxl-tabelle__kopf" role="row" style={{ gridTemplateColumns: raster }}>
        {spalten.map((s) => (
          <div key={s.schluessel} role="columnheader" className={s.rechts ? 'rechts' : undefined}>
            {s.titel}
          </div>
        ))}
      </div>
      {/* Kopf läuft bei zu schmaler Fläche seitlich mit den Zeilen mit */}
      <div ref={scrollRef} className="dxl-tabelle__scroll" style={{ height: hoehe }} onScroll={(e) => kopfRef.current && (kopfRef.current.scrollLeft = e.currentTarget.scrollLeft)}>
        <div style={{ height: v.getTotalSize(), position: 'relative' }}>
          {elemente.map((e) => {
            const zeile = zeilen[e.index];
            const stil = { transform: `translateY(${e.start}px)`, height: e.size, gridTemplateColumns: raster };
            if (!zeile) {
              return (
                <div key="laden" className="dxl-tabelle__zeile dxl-tabelle__laden" style={stil} role="row">
                  <span className="dxl-spinner" aria-hidden /> Weitere werden geladen
                </div>
              );
            }
            return (
              <div
                key={zeilenId(zeile)}
                role="row"
                aria-rowindex={e.index + 2}
                className={['dxl-tabelle__zeile', onZeile && 'klickbar', gewaehlt === zeilenId(zeile) && 'gewaehlt'].filter(Boolean).join(' ')}
                style={stil}
                tabIndex={onZeile ? 0 : undefined}
                onClick={onZeile ? () => onZeile(zeile) : undefined}
                onKeyDown={onZeile ? (ev) => ev.key === 'Enter' && onZeile(zeile) : undefined}
              >
                {spalten.map((s) => (
                  <div key={s.schluessel} role="cell" className={s.rechts ? 'rechts' : undefined}>
                    {s.zelle(zeile)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
